import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { NewQuoteDialog } from "@/components/quotes/NewQuoteDialog";
import { CustomerSearchSelect } from "@/components/quotes/CustomerSearchSelect";

function fmtEUR(n) { try { return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0); } catch { return "€ 0"; } }

const STATUS_COLORS = { draft: "bg-secondary", to_complete: "bg-amber-100 text-amber-900", to_approve: "bg-orange-100 text-orange-900", approved: "bg-emerald-100 text-emerald-900", sent: "bg-blue-100 text-blue-900", viewed: "bg-purple-100 text-purple-900", accepted: "bg-emerald-500 text-white", rejected: "bg-red-100 text-red-900", expired: "bg-zinc-200", cancelled: "bg-zinc-200", converted: "bg-emerald-600 text-white", superseded: "bg-zinc-100 text-zinc-500 line-through" };

export default function QuotesPage() {
  const { has } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [customerFilter, setCustomerFilter] = useState(null);
  const { data } = useQuery({ queryKey: ["quotes", q, customerFilter?.id], queryFn: async () => (await api.get("/quotes", { params: { q: q || undefined, customer_id: customerFilter?.id || undefined, limit: 200 } })).data });
  const createMut = useMutation({
    mutationFn: async (p) => (await api.post("/quotes", p)).data,
    onSuccess: (d) => { toast.success("Preventivo creato"); qc.invalidateQueries({ queryKey: ["quotes"] }); setOpen(false); navigate(`/quotes/${d.id}`); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  return (
    <div className="space-y-8" data-testid="quotes-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Modulo commerciale · M3</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-black tracking-tight">Preventivi</h1>
        </div>
        {has("quotes.create") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-sm h-11 gap-2" data-testid="new-quote-button"><Plus size={16} weight="bold" />Nuovo preventivo</Button></DialogTrigger>
            <NewQuoteDialog onSubmit={(p) => createMut.mutate(p)} loading={createMut.isPending} />
          </Dialog>
        )}
      </div>

      <Card className="rounded-sm border-border p-4 flex flex-col md:flex-row gap-3 md:items-center">
        <Input placeholder="Cerca cliente / preventivo (numero, nome, ragione sociale, telefono, email)…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm h-10 md:max-w-md" data-testid="quotes-search" />
        <div className="md:w-80"><CustomerSearchSelect value={customerFilter} onChange={setCustomerFilter} placeholder="Tutti i clienti" allowClear compact testid="quotes-customer-filter" /></div>
      </Card>

      <Card className="rounded-sm border-border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="overline">Numero</TableHead>
            <TableHead className="overline">Titolo</TableHead>
            <TableHead className="overline">Stato</TableHead>
            <TableHead className="overline">Totale</TableHead>
            {has("quotes.view_margins") && <TableHead className="overline">Margine</TableHead>}
            <TableHead className="overline">Prob.</TableHead>
            <TableHead className="overline">Aggiornato</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data?.items || []).map((qq) => (
              <TableRow key={qq.id} onClick={() => navigate(`/quotes/${qq.id}`)} className="cursor-pointer" data-testid={`quote-row-${qq.id}`}>
                <TableCell className="font-mono-plex text-xs">{qq.number}</TableCell>
                <TableCell className="text-sm">{qq.title || "—"}</TableCell>
                <TableCell><span className={`text-[10px] font-mono-plex uppercase px-2 py-1 rounded-sm ${STATUS_COLORS[qq.quote_status] || "bg-secondary"}`}>{qq.quote_status}</span></TableCell>
                <TableCell className="font-mono-plex">{fmtEUR(qq.total)}</TableCell>
                {has("quotes.view_margins") && <TableCell className="font-mono-plex text-xs text-[hsl(var(--brand-red))]">{qq.margin_abs != null ? `${fmtEUR(qq.margin_abs)} (${qq.margin_pct}%)` : "—"}</TableCell>}
                <TableCell><Badge variant="outline" className="rounded-sm">{qq.probability}%</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground">{qq.updated_at ? new Date(qq.updated_at).toLocaleDateString("it-IT") : ""}</TableCell>
              </TableRow>
            ))}
            {(data?.items || []).length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-muted-foreground">Nessun preventivo</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
